import { AlertCircle, CheckCircle2, Loader2, X } from "lucide-react";

export function AdminCard({ children, className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(11,53,84,0.04)] ${className}`}
    >
      {children}
    </div>
  );
}

export function AdminPageHead({ title, subtitle, children }) {
  return (
    <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-bka-navy lg:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1.5 text-sm text-slate-500">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export function AdminButton({ children, variant = "primary", className = "", testId, ...rest }) {
  const styles = {
    primary: "bg-bka-navy text-white hover:bg-bka-navyDeep",
    emerald: "bg-bka-emerald text-white hover:bg-bka-emeraldDark",
    ghost: "border border-slate-200 text-bka-navy hover:border-bka-emerald hover:text-bka-emerald",
    danger: "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
  }[variant];
  return (
    <button
      type="button"
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${styles} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  under_review: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
};

export function StatusBadge({ status, testId }) {
  const label = String(status || "").replace(/_/g, " ");
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold capitalize ${
        STATUS_STYLES[status] || "bg-slate-100 text-slate-600 border-slate-200"
      }`}
    >
      {label}
    </span>
  );
}

export function FilterTabs({ tabs, value, onChange, counts = {}, testIdPrefix = "filter" }) {
  return (
    <div className="mb-5 flex flex-wrap gap-2" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.value}
          type="button"
          role="tab"
          aria-selected={value === t.value}
          data-testid={`${testIdPrefix}-${t.value}`}
          onClick={() => onChange(t.value)}
          className={`rounded-xl px-3.5 py-2 text-sm font-bold transition-colors ${
            value === t.value
              ? "bg-bka-navy text-white"
              : "border border-slate-200 text-slate-600 hover:border-bka-emerald hover:text-bka-emerald"
          }`}
        >
          {t.label}
          {counts[t.value] !== undefined && (
            <span className={`ml-1.5 text-xs ${value === t.value ? "text-white/60" : "text-slate-400"}`}>
              ({counts[t.value]})
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export function AdminTable({ columns, children, testId }) {
  return (
    <div className="overflow-x-auto">
      <table data-testid={testId} className="w-full min-w-[720px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50/70">
            {columns.map((c) => (
              <th
                key={c}
                scope="col"
                className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-slate-500"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
}

export function EmptyState({ message, testId = "empty-state" }) {
  return (
    <div data-testid={testId} className="px-5 py-16 text-center text-sm text-slate-500">
      {message}
    </div>
  );
}

export function AdminSpinner({ label = "Loading" }) {
  return (
    <div data-testid="admin-loading" className="flex items-center gap-3 px-5 py-16 text-sm font-semibold text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}…
    </div>
  );
}

export function AdminBanner({ status, message, onDismiss, testId = "admin-banner" }) {
  if (!message) return null;
  const ok = status === "ok";
  return (
    <div
      data-testid={testId}
      role={ok ? "status" : "alert"}
      className={`mb-5 flex items-start gap-3 rounded-xl border p-4 text-sm font-semibold ${
        ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-700"
      }`}
    >
      {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />}
      <span className="flex-1">{message}</span>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss" data-testid={`${testId}-dismiss`}>
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function Drawer({ open, onClose, title, children, testId = "admin-drawer" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex justify-end" role="dialog" aria-modal="true" aria-label={title}>
      <button
        type="button"
        aria-label="Close panel"
        onClick={onClose}
        className="absolute inset-0 bg-bka-navyDeep/45 backdrop-blur-sm"
      />
      <div
        data-testid={testId}
        className="relative flex h-full w-full max-w-xl flex-col overflow-hidden bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <h2 className="font-display text-lg font-bold text-bka-navy">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testId}-close`}
            aria-label="Close"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:text-bka-navy"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
}

export function AdminField({ label, htmlFor, required, hint, children, className = "" }) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-bold text-bka-navy">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export const adminInputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-bka-ink transition-colors placeholder:text-slate-400 focus:border-bka-emerald focus:outline-none focus:ring-2 focus:ring-bka-emerald/20";
